import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { decodeJWT, isTokenValid, isTokenExpired, getUserFromToken } from '@/utils/jwt'

export interface User {
  id: number // User_id
  name: string
  email: string
  avatar?: string
  role?: number // Role_id del backend (1 = admin, 2 = usuario normal)
}

// Función para extraer iniciales del nombre o email
function getInitials(name: string, email: string): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }
  // Si no hay nombre, usar el email
  const emailParts = email.split('@')[0]
  if (emailParts.length >= 2) {
    return emailParts.substring(0, 2).toUpperCase()
  }
  return emailParts.substring(0, 1).toUpperCase() + emailParts.substring(1, 2)?.toUpperCase() || 'U'
}

export const useUserStore = defineStore('user', () => {
  // State
  const user = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const token = ref<string | null>(null)

  // Getters
  const userName = computed(() => user.value?.name || 'Guest')
  const userEmail = computed(() => user.value?.email || '')
  const isAdmin = computed(() => {
    // Verificar que el usuario existe, está autenticado y tiene rol 1
    if (!user.value || !isAuthenticated.value) {
      return false
    }
    // Verificar que el token sea válido antes de confiar en el rol
    if (token.value && !isTokenValid(token.value)) {
      return false
    }
    return user.value.role === 1
  })
  const userInitials = computed(() => {
    if (!user.value) return 'U'
    return getInitials(user.value.name, user.value.email)
  })

  // Actions
  function login(authResponse: { User_id: number; Email: string; Role_id: number; token: string; id: string }) {
    const authToken = authResponse.token || authResponse.id
    
    // Validar que el token sea válido antes de guardarlo
    if (!isTokenValid(authToken)) {
      throw new Error('Invalid or expired token')
    }
    
    // Decodificar JWT para obtener el nombre del usuario
    const decoded = decodeJWT(authToken)
    const userName = decoded?.usuario || authResponse.Email.split('@')[0] || 'User'
    
    const userData: User = {
      id: authResponse.User_id,
      name: userName,
      email: authResponse.Email,
      role: authResponse.Role_id,
    }
    
    user.value = userData
    token.value = authToken
    isAuthenticated.value = true
    localStorage.setItem('user', JSON.stringify(userData))
    localStorage.setItem('token', token.value)
  }

  function logout() {
    user.value = null
    token.value = null
    isAuthenticated.value = false
    localStorage.removeItem('user')
    localStorage.removeItem('token')
  }

  function updateProfile(userData: Partial<User>) {
    if (user.value) {
      user.value = { ...user.value, ...userData }
      localStorage.setItem('user', JSON.stringify(user.value))
    }
  }

  function initUser() {
    const savedUser = localStorage.getItem('user')
    const savedToken = localStorage.getItem('token')
    
    if (savedUser && savedToken) {
      try {
        // Verificar que el token sea válido antes de restaurar la sesión
        if (!isTokenValid(savedToken)) {
          logout()
          return
        }
        
        // Verificar que el token no esté expirado
        if (isTokenExpired(savedToken)) {
          logout()
          return
        }
        
        const parsedUser = JSON.parse(savedUser)
        
        // Verificar que el usuario del token coincida con el usuario guardado
        const tokenUser = getUserFromToken(savedToken)
        if (tokenUser && (tokenUser.id !== parsedUser.id || tokenUser.role !== parsedUser.role)) {
          logout()
          return
        }
        
        user.value = parsedUser
        token.value = savedToken
        isAuthenticated.value = true
      } catch {
        logout()
      }
    }
  }
  
  /**
   * Verifica si el token actual es válido y no ha expirado
   * Útil para verificar antes de hacer peticiones importantes
   */
  function validateToken(): boolean {
    if (!token.value) {
      return false
    }
    
    if (!isTokenValid(token.value)) {
      logout()
      return false
    }
    
    return true
  }

  /**
   * Para guards: devuelve true si el token es válido; si no, hace logout y devuelve false.
   * No expone el token fuera del store.
   */
  function ensureValidToken(): boolean {
    if (!token.value) return true
    if (!isTokenValid(token.value) || isTokenExpired(token.value)) {
      logout()
      return false
    }
    return true
  }

  return {
    user,
    isAuthenticated,
    userName,
    userEmail,
    isAdmin,
    userInitials,
    login,
    logout,
    updateProfile,
    initUser,
    validateToken,
    ensureValidToken,
  }
})
